/**
 * Coach als Overlay-Sheet — über den Coach-Tab (Toggle) erreichbar (T3.6).
 * Sitzt ÜBER der Tab-Leiste (sie bleibt sichtbar/tippbar). Mit Fokusfalle/
 * Escape/Scroll-Lock (useDialog) und Tastatur-Schutz: öffnet die Bildschirm-
 * tastatur, hebt sich das Sheet an, damit Eingabefeld + Kopfzeile sichtbar
 * bleiben (visualViewport; Konzept §6 Punkt 2).
 */
import { useEffect, useRef } from "preact/hooks";
import { dock, expanded, closeCoach, toggleExpanded } from "../state/coach";
import { CoachPanel } from "./CoachPanel";
import { useDialog } from "../ui/useDialog";
import { useBackClose } from "../ui/use-back-close";

function fontScale(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--font-scale");
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : 1.1;
}

/** Hebt das Sheet über die Bildschirmtastatur (iOS/Android). */
function useKeyboardInset(ref: { current: HTMLDivElement | null }, active: boolean) {
  useEffect(() => {
    if (!active) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const apply = () => {
      const el = ref.current;
      if (!el) return;
      // Vom Layout-Viewport verdeckter Bereich (≈ Tastaturhöhe), in CSS-px unter zoom.
      const occluded = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      if (occluded > 60) {
        el.style.bottom = `${Math.round(occluded / fontScale())}px`;
        el.style.maxHeight = `calc(var(--vh-voll) - var(--safe-top) - ${Math.round(occluded / fontScale())}px)`;
      } else {
        el.style.bottom = "";
        el.style.maxHeight = "";
      }
    };
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    apply();
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
      const el = ref.current;
      if (el) {
        el.style.bottom = "";
        el.style.maxHeight = "";
      }
    };
  }, [active]);
}

export function CoachSheet() {
  const open = dock.value === "sheet";
  const ref = useRef<HTMLDivElement>(null);
  useDialog(ref, closeCoach, open);
  useKeyboardInset(ref, open);
  // Android-Zurück schließt zuerst den Coach (Task F), statt wegzunavigieren.
  useBackClose(open, closeCoach);
  if (!open) return null;
  return (
    <>
      <div class="coach-backdrop" onClick={closeCoach} />
      <CoachPanel
        variant="sheet"
        rootRef={ref}
        onClose={closeCoach}
        expanded={expanded.value}
        onToggleExpanded={toggleExpanded}
      />
    </>
  );
}
